import React, { useState, useEffect } from 'react';
import { useToast } from '../../components/Toast';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { invalidateQueries } from '../../hooks/useCachedQuery';
import { logError } from '../../utils/logError';
import { ciftGirisOnayMetni } from '../../utils/ciftGiris';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

/**
 * GRN Detay Sayfası
 * - GRN bilgilerini gösterir (header, kalemler, kalite kontrol)
 * - BEKLEMEDE durumundaysa: Kalite Kontrole Gönder butonu
 * - Kalite kontrol yapıldıysa: Onayla / Reddet butonları
 */
const GRNDetayPage = () => {
  const navigate = useNavigate();
  const { id: routeGrnId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const selectedGrnId = routeGrnId || searchParams.get('id');

  const [grn, setGrn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();
  const [redNedeni, setRedNedeni] = useState('');
  const [showRedModal, setShowRedModal] = useState(false);

  const fetchGrnDetay = React.useCallback(() => {
    setLoading(true);
    fetch(`${API_URL}/api/grn/${selectedGrnId}`, { headers: getAuthHeaders() })
      .then(res => {
        if (!res.ok) throw new Error('GRN bulunamadı');
        return res.json();
      })
      .then(data => {
        setGrn(data);
        setLoading(false);
      })
      .catch(err => {
        logError('GRN detay yüklenemedi:', err);
        toast.error('GRN bulunamadı');
        setLoading(false);
      });
  }, [selectedGrnId, toast]);

  useEffect(() => {
    if (!selectedGrnId) {
      navigate('/grn/listesi');
      return;
    }
    fetchGrnDetay();
  }, [selectedGrnId, navigate, fetchGrnDetay, toast]);

  // GRN Onayla
  const handleOnayla = async (onayla = false) => {
    if (!grn) return;
    if (!user?.id) {
      toast.error('Kullanıcı bilgisi bulunamadı. Lütfen yeniden giriş yapın.');
      return;
    }
    setSubmitting(true);

    try {
      const url = `${API_URL}/api/grn/${grn.id}/approve${onayla ? '?onayla=true' : ''}`;
      const response = await fetch(url, { method: 'PATCH', headers: getAuthHeaders() });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Onaylama başarısız');
      }

      const data = await response.json();
      if (data.onay_gerekli) {
        setSubmitting(false);
        const onay = window.confirm(
          `${data.mesaj || 'Olası çift giriş — çift stok riski.'}\n\n${ciftGirisOnayMetni(data.cakismalar)}\n\nYine de onayla?`
        );
        if (onay) return handleOnayla(true);
        return;
      }

      toast.success('GRN onaylandı! Stok güncellendi.');
      invalidateQueries(['grn', 'stok', 'dashboard', 'siparis']);
      fetchGrnDetay(); // Detayı yenile
    } catch (err) {
      logError('Onaylama hatası:', err);
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // GRN İptal (Onaylanmış GRN'i geri al)
  const handleIptal = async () => {
    if (!grn) return;
    const msg = grn.durum === 'ONAYLANDI'
      ? 'Bu GRN ONAYLANMIŞ durumda.\n\nİptal edilirse:\n• Stok hareketleri geri alınır\n• Stok miktarları eski haline döner\n• Fiyat geçmişi kayıtları silinir\n\nDevam etmek istiyor musunuz?'
      : 'Bu GRN\'i iptal etmek istediğinize emin misiniz?';
    if (!window.confirm(msg)) return;
    setSubmitting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/grn/${grn.id}/cancel`,
        { method: 'PATCH', headers: getAuthHeaders() }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'İptal başarısız');
      toast.success(data.message);
      invalidateQueries(['grn', 'stok', 'dashboard', 'siparis']);
      fetchGrnDetay();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // GRN Reddet
  const handleReddet = async () => {
    if (!grn || !redNedeni.trim()) {
      toast.warning('Lütfen red nedenini yazınız.');
      return;
    }
    setSubmitting(true);

    try {
      const response = await fetch(
        `${API_URL}/api/grn/${grn.id}/reject?red_nedeni=${encodeURIComponent(redNedeni)}`,
        { method: 'PATCH', headers: getAuthHeaders() }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Reddetme başarısız');
      }

      toast.info('GRN reddedildi.');
      invalidateQueries(['grn', 'stok', 'dashboard', 'siparis']);
      setShowRedModal(false);
      setRedNedeni('');
      fetchGrnDetay();
    } catch (err) {
      logError('Reddetme hatası:', err);
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Durum badge
  const StatusBadge = ({ status }) => {
    const style = styles.statusColors[status] || { bg: '#e9ecef', color: '#495057' };
    return (
      <span style={{
        display: 'inline-block', padding: '4px 12px', borderRadius: radius.xl,
        fontSize: '12px', fontWeight: 'bold', background: style.bg, color: style.color
      }}>
        {status}
      </span>
    );
  };

  if (loading && !grn) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: colors.textSecondary }}>
        ⏳ GRN detayı yükleniyor...
      </div>
    );
  }

  if (!grn) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p>GRN bulunamadı.</p>
        <button onClick={() => navigate('/grn/listesi')} style={{ padding: '10px 20px', background: styles.primary, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>
          ← GRN Listesine Dön
        </button>
      </div>
    );
  }

  const kaliteKontrol = grn.kalite_kontrol;
  const kalemler = grn.kalemler || [];
  const qcYapildiMi = !!kaliteKontrol;
  const beklemede = grn.durum === 'BEKLEMEDE';

  return (
    <div>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${styles.primary}, ${styles.primaryDark})`,
        color: 'white', padding: '15px 25px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '20px' }}>📦 GRN Detay: {grn.grn_no}</h1>
          <span style={{ fontSize: '13px', opacity: 0.8 }}>
            Sipariş: {grn.siparis_no || '-'} | Tedarikçi: {grn.tedarikci_ad || '-'}
          </span>
        </div>
        <button
          onClick={() => navigate('/grn/listesi')}
          style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none', padding: '8px 15px', borderRadius: radius.sm, cursor: 'pointer' }}
        >
          ← Liste
        </button>
      </div>

      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        {/* GRN Bilgileri */}
        <div style={{ background: colors.surface, padding: '20px', borderRadius: radius.md, marginBottom: '20px', boxShadow: shadows.sm }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ margin: 0, color: styles.secondary }}>📋 GRN Bilgileri</h3>
            <StatusBadge status={grn.durum} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
            <div>
              <div style={{ fontSize: '12px', color: '#888', marginBottom: '2px' }}>GRN No</div>
              <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{grn.grn_no}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#888', marginBottom: '2px' }}>Sipariş No</div>
              <div style={{ fontWeight: 'bold' }}>{grn.siparis_no || '-'}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#888', marginBottom: '2px' }}>Tedarikçi</div>
              <div style={{ fontWeight: 'bold' }}>{grn.tedarikci_ad || '-'}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#888', marginBottom: '2px' }}>Teslim Tarihi</div>
              <div>{grn.teslim_tarihi ? new Date(grn.teslim_tarihi).toLocaleString('tr-TR') : '-'}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#888', marginBottom: '2px' }}>Teslim Alan</div>
              <div>{grn.teslim_alan_ad || '-'}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#888', marginBottom: '2px' }}>Lokasyon</div>
              <div>{grn.lokasyon_ad || '-'}</div>
            </div>
          </div>

          {/* Miktar Özeti */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginTop: '20px', padding: '15px', background: colors.backgroundAlt, borderRadius: radius.sm }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#888' }}>Sipariş Toplam</div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{(grn.siparis_toplam_kg || 0).toFixed(1)} kg</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#888' }}>Teslim Toplam</div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: styles.primary }}>{(grn.teslim_toplam_kg || 0).toFixed(1)} kg</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#888' }}>Fark</div>
              <div style={{
                fontSize: '20px', fontWeight: 'bold',
                color: (grn.fark_kg || 0) < 0 ? '#dc3545' : (grn.fark_kg || 0) > 0 ? '#28a745' : '#666'
              }}>
                {(grn.fark_kg || 0) > 0 ? '+' : ''}{(grn.fark_kg || 0).toFixed(1)} kg
                {grn.fark_orani != null && <span style={{ fontSize: '12px' }}> ({(grn.fark_orani * 100).toFixed(1)}%)</span>}
              </div>
            </div>
          </div>

          {grn.teslimat_notu && (
            <div style={{ marginTop: '15px', padding: '10px', background: '#fff3cd', borderRadius: radius.sm }}>
              <strong>📝 Teslimat Notu:</strong> {grn.teslimat_notu}
            </div>
          )}
          {grn.red_nedeni && (
            <div style={{ marginTop: '15px', padding: '10px', background: '#f8d7da', borderRadius: radius.sm }}>
              <strong>❌ Red Nedeni:</strong> {grn.red_nedeni}
            </div>
          )}
        </div>

        {/* Kalemler */}
        <div style={{ background: colors.surface, padding: '20px', borderRadius: radius.md, marginBottom: '20px', boxShadow: shadows.sm }}>
          <h3 style={{ margin: '0 0 15px 0', color: styles.secondary }}>📦 Kalemler ({kalemler.length})</h3>

          {kalemler.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: colors.textTertiary }}>Kalem bilgisi bulunamadı.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #ddd' }}>
                  <th style={{ padding: '7px', textAlign: 'left' }}>Malzeme</th>
                  <th style={{ padding: '7px', textAlign: 'right' }}>Sipariş</th>
                  <th style={{ padding: '7px', textAlign: 'right' }}>Teslim</th>
                  <th style={{ padding: '7px', textAlign: 'right' }}>Fark</th>
                  <th style={{ padding: '7px', textAlign: 'center' }}>Durum</th>
                  <th style={{ padding: '7px', textAlign: 'left' }}>Not</th>
                </tr>
              </thead>
              <tbody>
                {kalemler.map((k, idx) => {
                  const fark = (k.teslim_miktar || 0) - (k.siparis_miktar || 0);
                  return (
                    <tr key={idx} style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <td style={{ padding: '10px', fontWeight: 'bold' }}>{k.malzeme_ad || '-'}</td>
                      <td style={{ padding: '7px', textAlign: 'right' }}>{(k.siparis_miktar || 0).toFixed(2)}</td>
                      <td style={{ padding: '7px', textAlign: 'right', fontWeight: 'bold' }}>{(k.teslim_miktar || 0).toFixed(2)}</td>
                      <td style={{
                        padding: '7px', textAlign: 'right',
                        color: fark < -0.01 ? '#dc3545' : fark > 0.01 ? '#28a745' : '#666'
                      }}>
                        {fark > 0 ? '+' : ''}{fark.toFixed(2)}
                      </td>
                      <td style={{ padding: '7px', textAlign: 'center' }}>
                        <StatusBadge status={k.durum || 'OK'} />
                      </td>
                      <td style={{ padding: '10px', color: colors.textSecondary, fontSize: '13px' }}>{k.not_kalem || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Kalite Kontrol Bilgisi */}
        <div style={{ background: colors.surface, padding: '20px', borderRadius: radius.md, marginBottom: '20px', boxShadow: shadows.sm }}>
          <h3 style={{ margin: '0 0 15px 0', color: styles.secondary }}>✅ Kalite Kontrol</h3>

          {!qcYapildiMi ? (
            <div style={{ padding: '20px', textAlign: 'center', background: '#fff3cd', borderRadius: radius.sm }}>
              <div style={{ fontSize: '40px', marginBottom: '10px' }}>⏳</div>
              <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>Kalite kontrol henüz yapılmadı</div>
              {beklemede && (
                <button
                  onClick={() => navigate('/kalite-kontrol')}
                  style={{
                    padding: '10px 25px', background: styles.warning, color: '#333',
                    border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold'
                  }}
                >
                  ✅ Kalite Kontrol Yap →
                </button>
              )}
            </div>
          ) : (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' }}>
                <div style={{ textAlign: 'center', padding: '15px', background: colors.backgroundAlt, borderRadius: radius.sm }}>
                  <div style={{ fontSize: '12px', color: '#888' }}>Renk Skoru</div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{kaliteKontrol.renk_skoru || '-'}/5</div>
                </div>
                <div style={{ textAlign: 'center', padding: '15px', background: colors.backgroundAlt, borderRadius: radius.sm }}>
                  <div style={{ fontSize: '12px', color: '#888' }}>Koku Skoru</div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{kaliteKontrol.koku_skoru || '-'}/5</div>
                </div>
                <div style={{ textAlign: 'center', padding: '15px', background: colors.backgroundAlt, borderRadius: radius.sm }}>
                  <div style={{ fontSize: '12px', color: '#888' }}>Doku Skoru</div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{kaliteKontrol.doku_skoru || '-'}/5</div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '20px', marginBottom: '15px' }}>
                <div>{kaliteKontrol.yag_orani_ok ? '✅' : '❌'} Yağ Oranı</div>
                <div>{kaliteKontrol.sicaklik_ok ? '✅' : '❌'} Sıcaklık ({kaliteKontrol.sicaklik_c || '-'}°C)</div>
                <div>{kaliteKontrol.ambalaj_ok ? '✅' : '❌'} Ambalaj</div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 15px', background: kaliteKontrol.sonuc === 'ONAYLANDI' ? '#d4edda' : '#f8d7da', borderRadius: radius.sm }}>
                <span style={{ fontWeight: 'bold' }}>
                  Sonuç: <StatusBadge status={kaliteKontrol.sonuc} />
                </span>
                {kaliteKontrol.açıklama && <span style={{ color: colors.textSecondary, fontSize: '13px' }}>{kaliteKontrol.açıklama}</span>}
              </div>
            </div>
          )}
        </div>

        {/* Onay/Red Butonları */}
        {beklemede && qcYapildiMi && (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setShowRedModal(true)}
              disabled={submitting}
              style={{
                padding: '15px 30px', background: styles.danger, color: 'white',
                border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: '16px', fontWeight: 'bold'
              }}
            >
              ❌ Reddet
            </button>
            <button
              onClick={() => handleOnayla()}
              disabled={submitting || kaliteKontrol.sonuc === 'REDDEDILDI'}
              style={{
                flex: 1, padding: '15px', fontSize: '18px', fontWeight: 'bold',
                background: (submitting || kaliteKontrol.sonuc === 'REDDEDILDI') ? '#ccc' : styles.success,
                color: 'white', border: 'none', borderRadius: radius.sm,
                cursor: (submitting || kaliteKontrol.sonuc === 'REDDEDILDI') ? 'not-allowed' : 'pointer',
                boxShadow: shadows.md
              }}
            >
              {submitting ? '⏳ İşleniyor...' : '✅ ONAYLA ve STOĞA AL'}
            </button>
          </div>
        )}

        {/* İptal Butonu - ONAYLANDI veya BEKLEMEDE durumunda göster */}
        {grn && grn.durum !== 'IPTAL' && grn.durum !== 'REDDEDILDI' && (
          <div style={{ marginTop: '10px' }}>
            <button
              onClick={handleIptal}
              disabled={submitting}
              style={{
                padding: '12px 24px', background: '#dc3545', color: 'white',
                border: 'none', borderRadius: radius.sm, cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: '14px', fontWeight: 'bold', opacity: submitting ? 0.6 : 1
              }}
            >
              {grn.durum === 'ONAYLANDI' ? 'Onayı Geri Al ve İptal Et' : 'İptal Et'}
            </button>
          </div>
        )}

        {/* Red Modalı */}
        {showRedModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}>
            <div style={{ background: colors.surface, padding: '30px', borderRadius: radius.md, width: '500px', maxWidth: '92vw', boxSizing: 'border-box', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
              <h3 style={{ margin: '0 0 15px 0', color: styles.danger }}>❌ GRN Reddet</h3>
              <p style={{ color: colors.textSecondary }}>Bu GRN'i reddetme nedenini yazınız:</p>
              <textarea
                value={redNedeni}
                onChange={(e) => setRedNedeni(e.target.value)}
                placeholder="Kalite uygunsuzluğu, miktar eksikliği, vb."
                style={{ width: '100%', height: '100px', padding: '10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, marginBottom: '15px', resize: 'vertical' }}
              />
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setShowRedModal(false); setRedNedeni(''); }}
                  style={{ padding: '10px 20px', background: '#e0e0e0', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}
                >
                  İptal
                </button>
                <button
                  onClick={handleReddet}
                  disabled={submitting || !redNedeni.trim()}
                  style={{
                    padding: '10px 20px', background: styles.danger, color: 'white',
                    border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold'
                  }}
                >
                  {submitting ? 'İşleniyor...' : 'Reddet'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GRNDetayPage;
